"use client";

import { useState, useEffect } from "react";
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isSameDay,
  isToday,
} from "date-fns";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Plus, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { mockPosts, platformIcons, statusConfig } from "@/lib/mock-data";
import { useClient } from "@/lib/client-context";

export default function CalendarPage() {
  const { selectedClientId } = useClient();
  const [currentMonth, setCurrentMonth] = useState<Date | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Initialize "today" on the client only, to avoid SSR/hydration date mismatches
  useEffect(() => {
    const now = new Date();
    setCurrentMonth(now);
    setSelectedDate(now);
  }, []);

  if (!currentMonth || !selectedDate) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-muted-foreground">Loading calendar…</p>
      </div>
    );
  }

  const clientPosts = mockPosts.filter(
    (p) => selectedClientId === "all" || p.clientId === selectedClientId
  );

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const getPostsForDay = (day: Date) =>
    clientPosts.filter((p) => isSameDay(p.scheduledAt, day));

  const selectedDayPosts = getPostsForDay(selectedDate);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-5 border-b bg-card">
        <div className="flex items-center gap-5">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">
              {format(currentMonth, "MMMM yyyy")}
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Plan and schedule your content
            </p>
          </div>
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={() => {
                setCurrentMonth(new Date());
                setSelectedDate(new Date());
              }}
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Button className="shadow-sm shadow-primary/25">
          <Plus className="h-4 w-4 mr-2" />
          New Post
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 p-6 overflow-auto">
        <div className="grid grid-cols-7 mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div
              key={day}
              className="text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 py-3"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-px bg-border/60 rounded-xl overflow-hidden shadow-sm">
          {days.map((day) => {
            const selected = isSameDay(day, selectedDate);
            const dayIsToday = isToday(day);
            const outsideMonth = !isSameMonth(day, monthStart);
            const dayPosts = getPostsForDay(day);

            return (
              <div
                key={day.toISOString()}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedDate(day)}
                onKeyDown={(e) => e.key === "Enter" && setSelectedDate(day)}
                className={cn(
                  "bg-card p-2.5 min-h-[110px] text-left transition-all duration-150 hover:bg-accent/50 relative group cursor-pointer",
                  outsideMonth && "bg-muted/30",
                  selected && "bg-primary/10 outline outline-2 outline-primary"
                )}
              >
                <span
                  className={cn(
                    "inline-flex h-7 w-7 items-center justify-center rounded-full text-sm transition-colors",
                    dayIsToday && "bg-primary text-primary-foreground font-bold shadow-sm",
                    outsideMonth && !dayIsToday && "text-muted-foreground/40",
                    !dayIsToday && !outsideMonth && "text-foreground font-medium group-hover:bg-muted"
                  )}
                >
                  {format(day, "d")}
                </span>

                {dayPosts.length > 0 && (
                  <div className="mt-1 space-y-1">
                    {dayPosts.slice(0, 3).map((post) => {
                      const platform = platformIcons[post.platform];
                      const status = statusConfig[post.status];
                      return (
                        <Link
                          key={post.id}
                          href={`/content/posts/${post.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className={cn(
                            "flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[10px] font-medium truncate hover:ring-1 hover:ring-primary/40 transition-shadow",
                            platform?.bg
                          )}
                        >
                          <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", status.dot)} />
                          <span className={cn("truncate", platform?.color)}>
                            {post.content.slice(0, 22)}...
                          </span>
                        </Link>
                      );
                    })}
                    {dayPosts.length > 3 && (
                      <p className="text-[10px] text-muted-foreground pl-1.5">
                        +{dayPosts.length - 3} more
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected date panel */}
      <div className="border-t bg-card px-8 py-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">
                {format(selectedDate, "EEEE, MMMM d")}
              </h3>
              <p className="text-xs text-muted-foreground">
                {selectedDayPosts.length === 0
                  ? "No posts scheduled"
                  : `${selectedDayPosts.length} post${selectedDayPosts.length > 1 ? "s" : ""} scheduled`}
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm">
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Post
          </Button>
        </div>

        {selectedDayPosts.length > 0 && (
          <div className="space-y-2">
            {selectedDayPosts.map((post) => {
              const platform = platformIcons[post.platform];
              const status = statusConfig[post.status];
              return (
                <Link
                  key={post.id}
                  href={`/content/posts/${post.id}`}
                  className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 transition-colors cursor-pointer"
                >
                  {post.mediaUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={post.mediaUrl}
                      alt=""
                      className="h-10 w-10 rounded-md object-cover shrink-0"
                    />
                  )}
                  <span className={cn("h-2 w-2 rounded-full shrink-0", status.dot)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{post.content}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={cn("text-[11px] font-medium px-1.5 py-0.5 rounded", platform?.bg, platform?.color)}>
                        {platform?.label}
                      </span>
                      <span className={cn("text-[11px] font-medium px-1.5 py-0.5 rounded", status.bg, status.color)}>
                        {status.label}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        by {post.author}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
